import { query } from "gql-query-builder";
import { ResponseResult } from "../../../../../components/GenericAutocomplete/GenericAutocompleteTypes";
import { graphQlRequest } from "../../../../../config/axios/apis/graphql/graphql";
import { toLabelTagType } from "../../../../../config/axios/apis/labelTags/labelTagsAPI";
import { LabelTagOwnership, LabelTagType, PATIENT_LABEL_TAG_TYPES, WorkflowType } from "../../../../../utils/appEnums/AppEnums";
import { getWorkflowTemplateGroups } from "../../../../../config/axios/apis/workflow-builder/workflow-builder";
import store from "../../../../../store";

export const getLabelTags = (
    labelTagOwnership: `${LabelTagOwnership}`,
    labelTagType: `${LabelTagType}` | readonly LabelTagType[],
    limit: number,
    pageNumber: number,
    searchText: string,
    signal: AbortSignal,
    onlyActiveTags: boolean,
): Promise<ResponseResult> => {
    const defaultResult = { elements: [], total: 0, size: 0 } as ResponseResult;

    const filterValue: Record<string, any> = { type: Array.isArray(labelTagType) ? { in: labelTagType } : { equal: labelTagType } };

    if (searchText.length > 0) {
        Object.assign(filterValue, { name: { containsIgnoreCase: searchText } });
    }

    if (onlyActiveTags) {
        Object.assign(filterValue, { isActive: { equal: true } });
    }

    switch (labelTagOwnership) {
        case "own":
            Object.assign(filterValue, { createdByTenantId: { equal: store.getState().userReducer.tenant.id } });
            break;

        case "shared":
            Object.assign(filterValue, { createdByTenantId: { notEqual: store.getState().userReducer.tenant.id } });
            break;

        case "all":
            break;

        default:
            break;
    }

    return graphQlRequest(
        query({
            operation: "LabelTag",
            fields: [{ records: ["id", "name", "isActive", "type"] }, "recordsCount"],
            variables: {
                page: {
                    value: { limit, offset: pageNumber },
                    type: "LabelTagPageInput",
                },
                sort: {
                    value: { fields: { attr: "name", order: "ASC" } },
                    type: "LabelTagSortInput",
                },
                filter: {
                    value: filterValue,
                    type: "LabelTagFilterInput",
                },
            },
        }),
        false,
        { signal },
    )
        .then((response) => {
            if (response.data && response.data.data.LabelTag.records.length > 0) {
                defaultResult.elements = response.data.data.LabelTag.records.map((record: { id: number; name: string; type?: string | string[] }) => {
                    return {
                        id: record.id,
                        name: record.name,
                        type: toLabelTagType(record.type),
                    };
                });
                defaultResult.total = response.data.data.LabelTag.recordsCount;
                defaultResult.size = response.data.data.LabelTag.records.length;
            }
            return defaultResult;
        })
        .catch(() => defaultResult);
};

export const getPatientLabels = (limit: number, pageNumber: number, searchText: string, signal: AbortSignal): Promise<ResponseResult> => {
    return getLabelTags(LabelTagOwnership.OWN, PATIENT_LABEL_TAG_TYPES, limit, pageNumber, searchText, signal, true);
};

export const getImportLabels = (limit: number, pageNumber: number, searchText: string, signal: AbortSignal): Promise<ResponseResult> => {
    return getLabelTags(LabelTagOwnership.OWN, LabelTagType.IMPORT, limit, pageNumber, searchText, signal, true);
};

export const getJourneyLabels = (limit: number, pageNumber: number, searchText: string, signal: AbortSignal): Promise<ResponseResult> => {
    return getLabelTags(LabelTagOwnership.OWN, LabelTagType.JOURNEY, limit, pageNumber, searchText, signal, true);
};

export const getJourneyLabelsByTenantId = (
    tenantId: number,
    limit: number,
    pageNumber: number,
    searchText: string,
    signal: AbortSignal,
    // onlyActiveTags: boolean = false,
    onlyActiveTags: boolean,
    labelTagType: string = LabelTagType.JOURNEY,
): Promise<ResponseResult> => {
    const defaultResult = { elements: [], total: 0, size: 0 } as ResponseResult;

    const filterValue: Record<string, any> = { type: { equal: labelTagType }, createdByTenantId: { equal: tenantId } };
    if (searchText.length > 0) {
        Object.assign(filterValue, { name: { containsIgnoreCase: searchText } });
    }

    if (onlyActiveTags) {
        Object.assign(filterValue, { isActive: { equal: true } });
    }

    return graphQlRequest(
        query({
            operation: "LabelTag",
            fields: [{ records: ["id", "name", "isActive", "type"] }, "recordsCount"],
            variables: {
                page: {
                    value: { limit, offset: pageNumber },
                    type: "LabelTagPageInput",
                },
                sort: {
                    value: { fields: { attr: "name", order: "ASC" } },
                    type: "LabelTagSortInput",
                },
                filter: {
                    value: filterValue,
                    type: "LabelTagFilterInput",
                },
            },
        }),
        false,
        { signal },
    )
        .then((response) => {
            if (response.data && response.data.data.LabelTag.records.length > 0) {
                defaultResult.elements = response.data.data.LabelTag.records.map((record: { id: number; name: string }) => {
                    return {
                        id: record.id,
                        name: record.name,
                    };
                });
                defaultResult.total = response.data.data.LabelTag.recordsCount;
                defaultResult.size = response.data.data.LabelTag.records.length;
            }
            return defaultResult;
        })
        .catch(() => defaultResult);
};

export const getWorkflowResponseResult = (limit: number, pageNumber: number, searchText: string, signal: AbortSignal): Promise<ResponseResult> => {
    const defaultResult = { elements: [], total: 0, size: 0 } as ResponseResult;

    return getWorkflowTemplateGroups(
        {
            filterBy: [
                { key: "type", value: WorkflowType.CAMPAIGN, operator: "contains" },
                { key: "publishedWorkflowTemplateId", operator: "notBlank" },
                { key: "name", value: searchText, operator: "contains" },
            ],
        },
        { signal, params: { page: pageNumber, size: limit } },
        false,
    )
        .then((response) => {
            if (response.data && response.data.data.length > 0) {
                defaultResult.elements = response.data.data.map((record: { id: number; name: string }) => {
                    return {
                        id: record.id,
                        name: record.name,
                    };
                });
                defaultResult.total = response.data.totalElements;
                defaultResult.size = response.data.numberOfElements;
            }
            return defaultResult;
        })
        .catch(() => defaultResult);
};
